import { Box, SimpleGrid, Stack, Group, Badge, Avatar, Progress, RingProgress } from '@mantine/core';
import {
  IconUsers,
  IconBriefcase,
  IconCalendarTime,
  IconBuilding,
  IconArrowRight,
  IconTrendingUp,
  IconCake,
} from '@tabler/icons-react';
import { useNavigate } from 'react-router-dom';
import { Card, Text, Title, Button } from '../../../../components/ui';
import { hrStats, mockDepartments, mockLeaveRequests, mockAnnouncements, employeesByDepartment } from '../mockData/hr';
import { mockUsers } from '../../mockData';

const StatCard = ({
  title,
  value,
  subtitle,
  icon: Icon,
  color
}: {
  title: string;
  value: string | number;
  subtitle?: string;
  icon: React.ElementType;
  color: string;
}) => (
  <Card>
    <Group justify="space-between" mb="xs">
      <Text size="sm" c="dimmed">{title}</Text>
      <Box
        style={{
          width: 40,
          height: 40,
          borderRadius: 8,
          backgroundColor: `var(--mantine-color-${color}-light)`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Icon size={20} color={`var(--mantine-color-${color}-filled)`} />
      </Box>
    </Group>
    <Text size="xl" fw={700}>{value}</Text>
    {subtitle && (
      <Text size="xs" c="dimmed" mt="xs">{subtitle}</Text>
    )}
  </Card>
);

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric' }).format(date);
};

export default function HRDashboardPage() {
  const navigate = useNavigate();
  const pendingLeaves = mockLeaveRequests.filter(l => l.status === 'pending');
  const recentAnnouncements = mockAnnouncements.slice(0, 3);
  const totalEmployees = employeesByDepartment.reduce((acc, d) => acc + d.count, 0);

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>HR Dashboard</Title>
          <Text c="dimmed" size="sm">Manage employees and HR operations</Text>
        </Box>
        <Group>
          <Button variant="light" onClick={() => navigate('directory')}>Employee Directory</Button>
          <Button onClick={() => navigate('leave')}>Leave Requests</Button>
        </Group>
      </Group>

      {/* Stats Grid */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <StatCard
          title="Total Employees"
          value={hrStats.totalEmployees}
          subtitle={`+${hrStats.employeesChange} this month`}
          icon={IconUsers}
          color="blue"
        />
        <StatCard
          title="Open Positions"
          value={hrStats.openPositions}
          icon={IconBriefcase}
          color="green"
        />
        <StatCard
          title="Pending Leave Requests"
          value={hrStats.pendingLeaves}
          icon={IconCalendarTime}
          color="orange"
        />
        <StatCard
          title="Departments"
          value={hrStats.departmentCount}
          icon={IconBuilding}
          color="violet"
        />
      </SimpleGrid>

      {/* Employee Distribution and Announcements */}
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* Employee Distribution */}
        <Card>
          <Group justify="space-between" mb="md">
            <Text fw={600}>Employee Distribution</Text>
            <Button
              variant="subtle"
              size="xs"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('organization')}
            >
              View Org Chart
            </Button>
          </Group>
          <Group align="center" gap="xl">
            <RingProgress
              size={140}
              thickness={16}
              sections={employeesByDepartment.map(d => ({
                value: (d.count / totalEmployees) * 100,
                color: d.color,
              }))}
              label={
                <Box ta="center">
                  <Text size="xl" fw={700}>{totalEmployees}</Text>
                  <Text size="xs" c="dimmed">Total</Text>
                </Box>
              }
            />
            <Stack gap="xs" style={{ flex: 1 }}>
              {employeesByDepartment.map(dept => (
                <Group key={dept.department} justify="space-between">
                  <Group gap="sm">
                    <Box
                      w={12}
                      h={12}
                      style={{ borderRadius: 4, backgroundColor: `var(--mantine-color-${dept.color}-filled)` }}
                    />
                    <Text size="sm">{dept.department}</Text>
                  </Group>
                  <Text size="sm" fw={500}>{dept.count}</Text>
                </Group>
              ))}
            </Stack>
          </Group>
        </Card>

        {/* Announcements */}
        <Card>
          <Text fw={600} mb="md">Announcements</Text>
          <Stack gap="sm">
            {recentAnnouncements.map(announcement => (
              <Box
                key={announcement.id}
                p="sm"
                style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}
              >
                <Group justify="space-between" mb="xs">
                  <Badge size="xs" color={
                    announcement.priority === 'high' ? 'red' :
                    announcement.priority === 'medium' ? 'orange' : 'gray'
                  }>
                    {announcement.priority}
                  </Badge>
                  <Text size="xs" c="dimmed">{formatDate(announcement.createdAt)}</Text>
                </Group>
                <Text size="sm" fw={500}>{announcement.title}</Text>
                <Text size="xs" c="dimmed" lineClamp={2}>{announcement.content}</Text>
              </Box>
            ))}
          </Stack>
        </Card>
      </SimpleGrid>

      {/* Pending Leave Requests and Department Overview */}
      <SimpleGrid cols={{ base: 1, lg: 2 }}>
        {/* Pending Leave Requests */}
        <Card>
          <Group justify="space-between" mb="md">
            <Text fw={600}>Pending Leave Requests</Text>
            <Button
              variant="subtle"
              size="xs"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('leave')}
            >
              View All
            </Button>
          </Group>
          <Stack gap="sm">
            {pendingLeaves.length === 0 ? (
              <Box ta="center" py="xl">
                <Text c="dimmed" size="sm">No pending requests</Text>
              </Box>
            ) : (
              pendingLeaves.map(leave => (
                <Group key={leave.id} justify="space-between" p="sm" style={{ backgroundColor: 'var(--mantine-color-gray-light)', borderRadius: 8 }}>
                  <Group gap="sm">
                    <Avatar size="sm" radius="xl" color="blue">
                      {leave.employeeName.split(' ').map(n => n[0]).join('')}
                    </Avatar>
                    <Box>
                      <Text size="sm" fw={500}>{leave.employeeName}</Text>
                      <Text size="xs" c="dimmed">
                        {formatDate(leave.startDate)} - {formatDate(leave.endDate)} ({leave.days} days)
                      </Text>
                    </Box>
                  </Group>
                  <Badge color={
                    leave.type === 'vacation' ? 'blue' :
                    leave.type === 'sick' ? 'red' :
                    leave.type === 'parental' ? 'pink' : 'gray'
                  }>
                    {leave.type}
                  </Badge>
                </Group>
              ))
            )}
          </Stack>
        </Card>

        {/* Department Overview */}
        <Card>
          <Group justify="space-between" mb="md">
            <Text fw={600}>Departments</Text>
            <Button
              variant="subtle"
              size="xs"
              rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('organization')}
            >
              View All
            </Button>
          </Group>
          <Stack gap="sm">
            {mockDepartments.slice(0, 5).map(dept => (
              <Group key={dept.id} justify="space-between">
                <Group gap="sm">
                  <Box
                    w={12}
                    h={12}
                    style={{ borderRadius: 4, backgroundColor: `var(--mantine-color-${dept.color}-filled)` }}
                  />
                  <Text size="sm">{dept.name}</Text>
                </Group>
                <Group gap="md">
                  <Text size="xs" c="dimmed">{dept.employeeCount} employees</Text>
                  <Avatar size="xs" radius="xl" color={dept.color}>
                    {dept.headName.split(' ').map(n => n[0]).join('')}
                  </Avatar>
                </Group>
              </Group>
            ))}
          </Stack>
        </Card>
      </SimpleGrid>

      {/* Quick Stats */}
      <SimpleGrid cols={{ base: 1, sm: 2, lg: 4 }}>
        <Card>
          <Group gap="sm">
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                backgroundColor: 'var(--mantine-color-cyan-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconTrendingUp size={20} color="var(--mantine-color-cyan-filled)" />
            </Box>
            <Box>
              <Text size="xs" c="dimmed">Avg. Tenure</Text>
              <Text fw={600}>{hrStats.avgTenure} years</Text>
            </Box>
          </Group>
        </Card>
        <Card>
          <Group gap="sm">
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                backgroundColor: 'var(--mantine-color-red-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconUsers size={20} color="var(--mantine-color-red-filled)" />
            </Box>
            <Box>
              <Text size="xs" c="dimmed">Turnover Rate</Text>
              <Text fw={600}>{hrStats.turnoverRate}%</Text>
            </Box>
          </Group>
        </Card>
        <Card>
          <Group gap="sm">
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                backgroundColor: 'var(--mantine-color-pink-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconCake size={20} color="var(--mantine-color-pink-filled)" />
            </Box>
            <Box>
              <Text size="xs" c="dimmed">Upcoming Birthdays</Text>
              <Text fw={600}>{hrStats.upcomingBirthdays} this week</Text>
            </Box>
          </Group>
        </Card>
        <Card>
          <Group gap="sm">
            <Box
              style={{
                width: 40,
                height: 40,
                borderRadius: 8,
                backgroundColor: 'var(--mantine-color-green-light)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IconBriefcase size={20} color="var(--mantine-color-green-filled)" />
            </Box>
            <Box>
              <Text size="xs" c="dimmed">New Hires (Month)</Text>
              <Text fw={600}>{hrStats.employeesChange}</Text>
            </Box>
          </Group>
        </Card>
      </SimpleGrid>
    </Stack>
  );
}
